import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { connect } from "react-redux";
import { debounce } from "lodash";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { useSwipeable } from "react-swipeable";

// Local resources
import { ChartComponent } from "./TVChart";
import { ErrorBoundary, LinearDeterminate } from "./Utils";
import { setCandleType, sethistLen, setHorizon, setScore, setPlayMoney } from "../actions/settings";

// button disable duration 1.2 sec
const cooldownDuration = 1200;

const GymApp = ({
  user_id,
  isAuthenticated,
  candleType,
  histLen,
  horizon,
  score,
  playMoney,
  setScore,
  setPlayMoney,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  sessionStorage.setItem("loginRedirect", location.pathname);

  const API_URL = process.env.REACT_APP_API_BASE_URL;

  const [formData, setValues] = useState({
    ticker: "SPY",
    priceDataShow: [],
    priceDataHide: [],
    volDataShow: [],
    volDataHide: [],
    chartClose: 0,
    trend: "",
    dollarDelta: 0,
  });

  const [loading, setLoading] = useState(false);
  const [answer, showAnswer] = useState(false);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [lastVoteResult, setLastVoteResult] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });

  const [showInstructions, setShowInstructions] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [swipeEnabled, setSwipeEnabled] = useState(true);

  // Fetch market data (debounced)
  const debouncedFetchData = useRef(
    debounce(async (params) => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/api/loadGymData/`, { params });
        setValues({
          ticker: res.data.ticker,
          priceDataShow: res.data.priceDataShow,
          priceDataHide: res.data.priceDataHide,
          volDataShow: res.data.volDataShow,
          volDataHide: res.data.volDataHide,
          chartClose: res.data.chartClose,
          trend: res.data.trend,
          dollarDelta: res.data.dollarDelta,
        });
      } catch (err) {
        console.error("Error fetching data:", err);
        setAlert({ open: true, message: "Failed to fetch data. Please try again later.", severity: "error" });
      } finally {
        setLoading(false);
        setShowProgressBar(false);
        showAnswer(false);
      }
    }, cooldownDuration)
  ).current;

  useEffect(() => {
    debouncedFetchData({ candleType, histLen, horizon });
  }, [candleType, histLen, horizon]);

  useEffect(() => {
    const hasSeenInstructions = localStorage.getItem("hasSeenGymInstructions");
    if (!hasSeenInstructions) {
      setShowInstructions(true);
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  // Handle voting
  const handleVote = (vote) => {
    if (!formData.trend) {
      handleNextChart();
      return;
    }

    if ((vote === "up" && formData.trend === "up") || (vote === "down" && formData.trend === "down")) {
      setScore(score + 1);
      setPlayMoney(playMoney + formData.dollarDelta);
      setLastVoteResult("correct");
    } else {
      setScore(score - 1);
      setPlayMoney(playMoney - formData.dollarDelta);
      setLastVoteResult("wrong");
    }

    showAnswer(true);
    setSwipeEnabled(false);
    // Automatically advance after 3 seconds
    setTimeout(() => {
      handleNextChart();
    }, 3000);
  };

  // Load next chart
  const handleNextChart = useCallback(() => {
  setShowProgressBar(true);
  debouncedFetchData({ candleType, histLen, horizon });
  showAnswer(false);
  setLastVoteResult(null);
  setSwipeEnabled(true);
  }, [debouncedFetchData, candleType, histLen, horizon]);

  // Save user progress
  const handleSaveProgress = useCallback(async () => {
    if (!isAuthenticated) {
      if (window.confirm("You need an account to save progress. Would you like to log in or sign up?")) {
        navigate("/login");
      }
      return;
    }

    try {
      const payload = {
        userid: user_id,
        score: score,
        balance: Math.round(((playMoney) + Number.EPSILON) * 100) / 100,
      };
      // NOTE: This assumes you have a backend endpoint to handle this POST request.
      await axios.post(`${API_URL}/api/scores/`, payload);
      setAlert({ open: true, message: "Progress saved successfully!", severity: "success" });
    } catch (err) {
      console.error("Error saving gym progress:", err);
      setAlert({ open: true, message: "Failed to save progress. Please try again.", severity: "error" });
    }
  }, [API_URL, isAuthenticated, playMoney, score, user_id, navigate]);

  // Reset user progress
  const handleResetProgress = useCallback(() => {
    if (window.confirm("Are you sure you want to reset your score and balance? This action cannot be undone.")) {
      const initialPlayMoney = 10000; // Default starting balance
      setScore(0);
      setPlayMoney(initialPlayMoney);
      handleNextChart(); // Fetch a new chart after resetting
      setAlert({ open: true, message: "Progress has been reset.", severity: "info" });
    }
  }, [setScore, setPlayMoney, handleNextChart]);

  // Listen for a global reset event
  useEffect(() => {
    window.addEventListener("resetGymProgress", handleResetProgress);
    return () => {
      window.removeEventListener("resetGymProgress", handleResetProgress);
    };
  }, [handleResetProgress]);

  // Listen for a global save event, which can be dispatched from another component like the main navbar
  useEffect(() => {
    window.addEventListener("saveGymProgress", handleSaveProgress);
    return () => {
      window.removeEventListener("saveGymProgress", handleSaveProgress);
    };
  }, [handleSaveProgress]);

  // Swipe handlers
  const swipeThreshold = window.innerWidth * 0.1; // 10% of screen

  const swipeHandlers = useSwipeable({
    onSwiping: ({ deltaX }) => {
      if (!isAnimating && swipeEnabled) {
        setSwipeOffset(deltaX);
      }
    },
    onSwiped: ({ dir, deltaX }) => {
      if (swipeEnabled && Math.abs(deltaX) > swipeThreshold) {
        if (dir === "Left") {
          setIsAnimating(true);
          setSwipeOffset(-window.innerWidth);
          setTimeout(() => {
            handleVote("down");
            setSwipeOffset(0);
            setIsAnimating(false);
          }, 300);
        } else if (dir === "Right") {
          setIsAnimating(true);
          setSwipeOffset(window.innerWidth);
          setTimeout(() => {
            handleVote("up");
            setSwipeOffset(0);
            setIsAnimating(false);
          }, 300);
        }
      } else {
        setSwipeOffset(0); // snap back
      }
    },
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  const handleCloseInstructions = () => {
    setShowInstructions(false);
    localStorage.setItem("hasSeenGymInstructions", "true");
  };

return (
  <Box
    display="flex"
    flexDirection="column"
    sx={{
      bgcolor: "#f5f5f5",
      height: "95vh",        // fill full viewport
      width: "100vw",
      m: 0,
      p: 0,
      overflow: "hidden",
    }}
  >
    {/* Chart */}
    <Box
      sx={{
        flex: 1,
        position: "relative",
        width: "100%",
        height: "100%",
      }}
    >
      <Card
        sx={{
          height: "100%", // take all remaining height
          bgcolor: "white",
          boxShadow: 0,
          borderRadius: 0,
          width: "100vw",
          transform: `translateX(${swipeOffset}px)`,
          transition: isAnimating ? "transform 0.3s ease" : "none",
        }}
        style={{ // Note: `style` prop is used for properties not available in `sx`
          touchAction: "pan-y",
          overflow: "hidden",
          margin: 0,
          padding: 0,
        }}
        {...(swipeEnabled ? swipeHandlers : {})}
      >
        <CardContent sx={{ p: 0, m: 0, height: "100%", width: "100%" }}>
          <ErrorBoundary>
            <div style={{ height: "100%", width: "100%", margin: 0, padding: 0 }}>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <CircularProgress />
                </Box>
              ) : showProgressBar ? (
                <LinearDeterminate />
              ) : (
                <>
                  {answer && (
                    <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%", zIndex: 2, py: 1, bgcolor: "rgba(255,255,255,0.85)", textAlign: "center" }}>
                      <strong style={{ color: lastVoteResult === "correct" ? "#2e7d32" : "#d32f2f" }}>
                        Δ ${formData.dollarDelta.toFixed(2)}
                      </strong>
                    </Box>
                  )}
                  <ChartComponent
                    tickerSymbol=""
                    candleSeriesData={answer ? formData.priceDataHide : formData.priceDataShow}
                    volumeSeriesData={answer ? formData.volDataHide : formData.volDataShow}
                  />
                </>
              )}
            </div>
          </ErrorBoundary>
          {showProgressBar && <LinearDeterminate />}
        </CardContent>
      </Card>
      {/* Floating Skip Button */}
      <Button
        size="small"
        variant="contained"
        onClick={handleNextChart}
        disabled={loading || showProgressBar || isAnimating}
        sx={{
          position: "absolute",
          top: 5,
          left: 5,
          // bottom: 16,
          // right: 16,
          bgcolor: "white",
          color: "black",
          zIndex: 10, // Ensure it's above the card
          "&:hover": { bgcolor: "#f0f0f0" },
        }}
      >
        Skip Chart
      </Button>
    </Box>

    {/* Instructions Dialog */}
    <Dialog open={showInstructions} onClose={handleCloseInstructions} aria-labelledby="instructions-dialog-title">
      <DialogTitle id="instructions-dialog-title">Welcome to the Chart Gym!</DialogTitle>
      <DialogContent dividers>
        <Typography gutterBottom>
          Practice your chart-reading skills. A historical chart will be shown, and your goal is to predict if the price will go <strong>Up</strong> or <strong>Down</strong>.
        </Typography>
        <Typography gutterBottom><strong>How to Play:</strong></Typography>
        <ul>
          <li><Typography variant="body2"><strong>Swipe Right</strong> to vote "Up".</Typography></li>
          <li><Typography variant="body2"><strong>Swipe Left</strong> to vote "Down".</Typography></li>
          <li><Typography variant="body2">Use the <strong>Skip Chart</strong> button to get a new chart without voting.</Typography></li>
        </ul>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseInstructions} variant="contained">Got It!</Button>
      </DialogActions>
    </Dialog>

    {/* Alerts */}
    <Snackbar
      open={alert.open}
      autoHideDuration={3000}
      onClose={() => setAlert({ ...alert, open: false })}
    >
      <Alert severity={alert.severity}>{alert.message}</Alert>
    </Snackbar>

  </Box>
);
  
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
  user_id: state.auth.user_id,
  candleType: state.settings.candleType,
  histLen: state.settings.histLen,
  horizon: state.settings.horizon,
  score: state.settings.score,
  playMoney: state.settings.playMoney,
});

const mapDispatchToProps = { setCandleType, sethistLen, setHorizon, setScore, setPlayMoney };

export default connect(mapStateToProps, mapDispatchToProps)(GymApp);
